import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, XCircle } from "lucide-react";

const Login = () => {
  const { user, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pendingMessage = location.state?.pendingMessage;
  const errorMessage = location.state?.errorMessage;

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left - Image */}
      <div 
        className="login-image hidden md:block"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')"
        }}
      >
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            Back Office
          </h2>
          <p className="text-lg text-slate-200 max-w-md">
            En PD Travel, nuestro Back Office es la base de todo. Una plataforma integral que reúne administración, logística y ventas, con herramientas pensadas para simplificar procesos y hacer nuestro trabajo más eficiente cada día.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img 
                src="https://pdtravelmex.com/wp-content/uploads/2026/03/Diseno-sin-titulo-52.png" 
                alt="PD Travel" 
                className="h-16 object-contain"
              />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed max-w-sm mx-auto">
            Nuestro hub del día a día: un sistema integral que une administración, logística y ventas, facilitando cada proceso
            </p>
          </div>

          {/* Pending Approval Message */}
          {pendingMessage && (
            <Card className="border-amber-300 bg-amber-50 shadow-md" data-testid="pending-message">
              <CardContent className="p-5">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-1">Cuenta Pendiente de Aprobación</h3>
                    <p className="text-sm text-amber-700 leading-relaxed">{pendingMessage}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Deactivated Account Message */}
          {errorMessage && (
            <Card className="border-red-300 bg-red-50 shadow-md" data-testid="error-message">
              <CardContent className="p-5">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-800 mb-1">Cuenta Desactivada</h3>
                    <p className="text-sm text-red-700 leading-relaxed">{errorMessage}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-heading">Iniciar Sesión</CardTitle>
              <CardDescription>
                Accede con tu cuenta de Google corporativa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={loginWithGoogle}
                className="w-full h-12 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm"
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar con Google
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-slate-500 mt-4">
                  Solo usuarios con correo <strong>@pdtravelmex.com</strong> pueden acceder
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-400">
            Al iniciar sesión, aceptas los términos y condiciones del servicio
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
